/**
 * OCT28 #8 (field test 2026-10-28) — the ONE run state a Dayboard ticket card presents, for both of its borders.
 *
 * The card used to colour its left stripe from `status === 'Active' && actually_started` (green even after the timer
 * stopped) and its outer border with rollover winning over a running or paused timer, so one card could say "running"
 * and "rollover" at once. Both now read this, derived only from the authoritative timer projection
 * (`tickets.timer_status`); no second status authority is created. Precedence: running > paused > rollover > idle.
 */
export type CardRunState = 'running' | 'paused' | 'rollover' | 'idle';

export function cardTimerRunState(input: {
  timerStatus: string | null | undefined;
  /** Marketing film-mode presentation override (presentation only). */
  filmOverride?: boolean;
  isRollover: boolean;
}): CardRunState {
  if (input.timerStatus === 'running' || input.filmOverride) return 'running';
  if (input.timerStatus === 'paused') return 'paused';
  if (input.isRollover) return 'rollover';
  return 'idle';
}

/**
 * OCT28 RECOVERY #1: an encrypted field envelope (`v1:<iv>:<ct>` legacy, `v2:<ver>:<iv>:<ct>` — the Edge field-encryption envelope).
 * Production stores EVERY vehicle VIN / plate this way; such a value is never shown. (The first #1 fix hid `v1:` only,
 * so a `v2:` plate would have printed its ciphertext on the card.)
 */
export function isEncryptedEnvelope(value: string | null | undefined): boolean {
  return typeof value === 'string' && /^v\d+:/.test(value.trim());
}

/** OCT28 #1: "ABC123 · TX" — the plate with its issuing state, or the plate alone; nothing for an encrypted value. */
export function cardPlateLabel(plate: string | null | undefined, jurisdiction: string | null | undefined): string | null {
  if (!plate || !plate.trim() || isEncryptedEnvelope(plate)) return null;
  const state = jurisdiction?.trim().replace(/^US-/i, '');
  return state ? `${plate} · ${state}` : plate;
}

/** OCT28 RECOVERY #1: the VIN as the card shows it — the decrypted value, never an envelope or blank. */
export function cardVinLabel(vin: string | null | undefined): string | null {
  if (!vin || !vin.trim() || isEncryptedEnvelope(vin)) return null;
  return vin.trim().toUpperCase();
}
